import { useCallback, useState } from "react";

import { useFragment } from "@apollo/client";
import { MenuItem } from "@blueprintjs/core";
import { Link } from "react-router";

import { Box, Flex } from "@/components/layout/flexbox";
import { CollectionTitle } from "@/components/pages_logged_in/roleplays/CollectionTitle";
import { CardAboveXs } from "@/components/pieces/CardAboveXs";
import { ConfirmDialog } from "@/components/pieces/dialog/ConfirmDialog";
import { MoreMenu } from "@/components/pieces/more/MoreMenu";
import { CARD_CLASSES } from "@/css/constants";
import {
  ScenarioCollectionsFragment,
  ScenarioCollectionsFragmentDoc,
  ScenarioDocument,
  useRemoveCollectionScenarioMutation,
} from "@/graphql";
import { CollectionsRoleplay, FullScenario } from "@/types/roleplays";
import { ensureNonNullArray } from "@/utils/array";

interface CompactCollectionCardProps {
  collection: CollectionsRoleplay;
  onRemoveFromCollection: (collection: CollectionsRoleplay) => void;
  canRemove: boolean;
}

function CompactCollectionCard({
  collection,
  onRemoveFromCollection,
  canRemove,
}: CompactCollectionCardProps) {
  const handleRemoveFromCollection = useCallback(
    () => onRemoveFromCollection(collection),
    [onRemoveFromCollection, collection],
  );

  return (
    <Link to={`/roleplays/${collection.slug}`} className="color-inherit">
      <Flex
        justifyContent="space-between"
        alignItems="flex-start"
        className={CARD_CLASSES}
        p={24}
        style={{ gap: 32 }}
        backgroundColor="#FFF"
      >
        <CollectionTitle collection={collection} />
        {canRemove && (
          <MoreMenu>
            <MenuItem
              intent="danger"
              text="Remove from Collection"
              onClick={handleRemoveFromCollection}
            />
          </MoreMenu>
        )}
      </Flex>
    </Link>
  );
}

interface ScenarioCollectionsProps {
  scenario: FullScenario;
}

export function ScenarioCollections({ scenario }: ScenarioCollectionsProps) {
  const { complete, data } = useFragment<ScenarioCollectionsFragment>({
    fragment: ScenarioCollectionsFragmentDoc,
    from: scenario,
  });

  const [removeFromCollection] = useRemoveCollectionScenarioMutation();
  const [collectionToRemove, setCollectionToRemove] =
    useState<CollectionsRoleplay>();

  const handleRemoveFromCollection = useCallback(
    (collection: CollectionsRoleplay) => {
      setCollectionToRemove(collection);
    },
    [setCollectionToRemove],
  );

  const handleConfirmRemoveFromCollection = useCallback(() => {
    removeFromCollection({
      variables: {
        scenarioIds: [scenario.id],
        roleplaySlug: collectionToRemove?.slug ?? "",
      },
      refetchQueries: [
        {
          query: ScenarioDocument,
          variables: { scenarioSlug: scenario.slug },
        },
      ],
      onCompleted: () => {
        setCollectionToRemove(undefined);
      },
    });
  }, [
    removeFromCollection,
    collectionToRemove,
    scenario,
    setCollectionToRemove,
  ]);

  const collections = ensureNonNullArray(complete ? data.collections : []);

  return (
    collections.length > 0 && (
      <CardAboveXs>
        <Flex flexDirection="column" style={{ gap: 16 }}>
          <Box fontWeight={600} fontSize={18}>
            In Collections
          </Box>
          {collections.map((collection) => (
            <CompactCollectionCard
              key={collection.id}
              collection={collection}
              onRemoveFromCollection={handleRemoveFromCollection}
              canRemove={collection.canEdit ?? false}
            />
          ))}
          {collectionToRemove && (
            <ConfirmDialog
              onConfirm={handleConfirmRemoveFromCollection}
              onCancel={() => setCollectionToRemove(undefined)}
              confirmLabel="Remove"
              cancelLabel="Nevermind"
            >
              <p>
                Are you sure you want to remove <b>{scenario.name}</b> from{" "}
                <b>{collectionToRemove?.name}</b>?
              </p>
            </ConfirmDialog>
          )}
        </Flex>
      </CardAboveXs>
    )
  );
}
